// Web Push: suscribir/desuscribir el browser actual + persistir en Supabase.
// 2026-06-22: fix "could not retrieve the public key" — limpiar sub vieja, esperar SW, retry.
import { supabase } from './supabase'

// VAPID regenerado 2026-06-22 — el par anterior generaba "could not retrieve the public key"
// persistente en Chrome (probablemente cacheado en FCM como bloqueado).
const VAPID_PUBLIC_KEY = 'BEKCKjkN6IZSIQB87bJ1bKbjfVVc6IUWMjst6Lwufx-Q-P38rW38OnzSNBeonM3IuRr6tjFaybyhbiMCHQa4aPE'

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)))
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

export async function pushPermissionState() {
  if (!pushSupported()) return 'unsupported'
  return Notification.permission // 'default' | 'granted' | 'denied'
}

export async function pushSubscribed() {
  if (!pushSupported()) return false
  try {
    const reg = await navigator.serviceWorker.ready
    const sub = await reg.pushManager.getSubscription()
    return !!sub
  } catch {
    return false
  }
}

// Espera a que el SW tome control efectivo de la página (a veces no lo hace al primer load).
async function esperarSWActivo(timeoutMs = 5000) {
  if (!('serviceWorker' in navigator)) throw new Error('Service Worker no soportado')

  // Si no hay SW registrado, intentar registrarlo (caso PWA fresh install)
  let reg = await navigator.serviceWorker.getRegistration()
  if (!reg) {
    try { reg = await navigator.serviceWorker.register('/sw.js') } catch {}
  }

  // Esperar que esté listo
  const ready = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, rej) => setTimeout(() => rej(new Error('Timeout esperando Service Worker')), timeoutMs))
  ])
  return ready
}

export async function pushSubscribe() {
  if (!pushSupported()) throw new Error('Tu navegador no soporta notificaciones push')

  // 1) Permiso del browser
  let perm = Notification.permission
  if (perm === 'default') perm = await Notification.requestPermission()
  if (perm !== 'granted') throw new Error('Tenés que permitir las notificaciones desde el navegador')

  // 2) Esperar SW activo (sin esto, subscribe falla con 'could not retrieve the public key')
  const reg = await esperarSWActivo()

  // 3) Limpiar suscripción VIEJA (si existe con otra VAPID o corrupta).
  //    Esta es la causa #1 del error en producción: el browser tenía una sub anterior
  //    y al pedir nueva subscribe con key distinta el push service rechaza.
  const existing = await reg.pushManager.getSubscription()
  if (existing) {
    try {
      // Limpio también del backend para no quedar con basura
      const oldEndpoint = existing.endpoint
      await existing.unsubscribe()
      await supabase.from('push_subscriptions').delete().eq('endpoint', oldEndpoint).catch(() => {})
    } catch (e) {
      console.warn('No pude limpiar suscripción vieja, sigo:', e)
    }
  }

  // 4) Suscribir con retry suave (a veces FCM/Mozilla está lento al primer intento)
  let sub = null
  let ultimoError = null
  for (let intento = 0; intento < 3; intento++) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      })
      break
    } catch (e) {
      ultimoError = e
      console.warn(`pushSubscribe intento ${intento + 1}/3 falló:`, e?.message || e)
      if (intento < 2) await new Promise(r => setTimeout(r, 1000 + 500 * intento))
    }
  }
  if (!sub) {
    const msg = String(ultimoError?.message || ultimoError || '')
    if (msg.toLowerCase().includes('public key')) {
      throw new Error(
        'No pude conectar con el servicio de notificaciones del navegador. ' +
        'Probá: 1) cerrar y abrir Chrome, 2) revisar Configuración > Privacidad > Notificaciones, ' +
        '3) reintentar en 1 minuto. Si seguís con error: tu red puede estar bloqueando FCM (fcm.googleapis.com).'
      )
    }
    throw ultimoError || new Error('Falló la suscripción push')
  }

  // 5) Persistir en Supabase para que el backend pueda mandar push
  const subJson = sub.toJSON()
  const p256dh = subJson.keys?.p256dh
  const authKey = subJson.keys?.auth
  const endpoint = subJson.endpoint

  // Obtener user con retry: en subdominios crm./repartidor. la sesión a veces tarda
  // unos ms en hidratar desde localStorage cuando el subscribe corre temprano.
  let user = null
  for (let i = 0; i < 6; i++) {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) { user = session.user; break }
    if (i === 5) {
      // Último intento: ir al server por si localStorage está raro
      const { data: { user: u } } = await supabase.auth.getUser()
      if (u) { user = u; break }
    }
    await new Promise(r => setTimeout(r, 400))
  }
  if (!user) throw new Error('No estás logueado. Recargá la página y reintentá.')

  const { error } = await supabase.from('push_subscriptions').upsert({
    user_id: user.id,
    endpoint,
    p256dh,
    auth_key: authKey,
    user_agent: navigator.userAgent,
    enabled: true,
    failures_count: 0,
    updated_at: new Date().toISOString(),
  }, { onConflict: 'endpoint' })
  if (error) throw error

  return { ok: true, endpoint }
}

export async function pushUnsubscribe() {
  if (!pushSupported()) return
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (sub) {
    const endpoint = sub.endpoint
    await sub.unsubscribe()
    await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint)
  }
}

// Reset completo: limpia toda referencia push del browser + del backend para este user.
// Útil cuando push subscribe falla por estado inconsistente del browser ("could not
// retrieve the public key" persistente, SW viejo cacheado, sub fantasma, etc.)
// Después de llamar esta función hay que recargar la página con Cmd+Shift+R.
export async function pushReset() {
  if (!pushSupported()) return { ok: false, error: 'Browser no soporta push' }
  const pasos = []

  // 1) Unsubscribe + borrar todas las subs de este browser
  try {
    const regs = await navigator.serviceWorker.getRegistrations()
    for (const reg of regs) {
      const sub = await reg.pushManager.getSubscription().catch(() => null)
      if (sub) {
        const endpoint = sub.endpoint
        try { await sub.unsubscribe() } catch {}
        try { await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint) } catch {}
        pasos.push(`Sub borrada: ${endpoint.slice(0, 40)}…`)
      }
    }
  } catch (e) { pasos.push(`getSubscription falló: ${e}`) }

  // 2) Unregister todos los SW del sitio
  try {
    const regs = await navigator.serviceWorker.getRegistrations()
    for (const reg of regs) {
      await reg.unregister()
      pasos.push(`SW unregistered: ${reg.scope}`)
    }
  } catch (e) { pasos.push(`unregister falló: ${e}`) }

  // 3) Borrar TODAS las subs de este user del backend (por si quedaron viejas)
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user
    if (user) {
      const { error } = await supabase.from('push_subscriptions').delete().eq('user_id', user.id)
      if (!error) pasos.push('Backend: subs del user borradas')
    }
  } catch (e) { pasos.push(`backend delete falló: ${e}`) }

  // 4) Borrar caches del SW (CACHE_VERSION viejo puede tener archivos rotos)
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      for (const k of keys) {
        await caches.delete(k)
        pasos.push(`Cache borrado: ${k}`)
      }
    }
  } catch (e) { pasos.push(`caches.delete falló: ${e}`) }

  return { ok: true, pasos }
}

// Test: dispara una push de prueba al usuario actual (usa la edge function send-push)
// IMPORTANTE: title NO debe llevar emoji al principio. Android lo usa como ícono visual
// y oculta el isotipo Limpack. Los emojis van en el body si hace falta.
export async function pushTest() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Sin sesión')
  const { data, error } = await supabase.functions.invoke('send-push-admin', {
    body: { user_id: user.id, title: 'Limpack', body: 'Notificaciones activas. Te avisamos cuando llegue un mensaje.', url: '/admin/crm', test: true },
  })
  if (error) throw error
  return data
}
